import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../utility/ToastMessage";
import { updateStudentAPI } from "../../services/portal/admin/studentService";
import { updateMyDetailsAPI } from "../../services/portal/student/studentService";

const StudentDetailsManage = ({ studentData, isAdminView = false }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Details
    firstName: "",
    lastName: "",
    nic: "",
    email: "",
    birthday: "",
    gender: "",
    addressLine1: "",
    street: "",
    city: "",
    isPhysical: "",
    institute: "",
    district: "",
    batch: "",
    school: "",
    indexNo: "",

    // Guardian Details
    guardianName: "",
    relationship: "",
    guardianContactNo: "",
  });

  useEffect(() => {
    if (studentData) {
      setFormData({
        firstName: studentData.FirstName || "",
        lastName: studentData.LastName || "",
        nic: studentData.NIC || "",
        email: studentData.Email || "",
        birthday: studentData.BirthDay,
        gender: studentData.Gender || "",
        addressLine1: studentData.AddressLine1 || "",
        street: studentData.Street || "",
        city: studentData.City || "",
        guardianName: studentData.GuardianName || "",
        relationship: studentData.Relationship || "",
        postalCode: studentData.PostalCode || "",
        guardianContactNo: studentData.GuardianContactNo || "",
        isPhysical: studentData.IsPhysical ? "1" : "0",
        institute: studentData.Institute || "",
        district: studentData.District || "",
        batch: studentData.Batch || "",
        school: studentData.School || "",
        indexNo: studentData.IndexNo || "",
      });
    }
  }, [studentData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    var errorMsg = "";

    if (!formData.firstName.trim()) {
      errorMsg = "First Name";
    }
    if (!formData.lastName.trim()) {
      errorMsg = errorMsg + ", Last Name";
    }
    if (!formData.nic.trim()) {
      errorMsg = errorMsg + ", NIC";
    }
    if (!formData.birthday) {
      errorMsg = errorMsg + ", Birthday";
    }
    if (!formData.gender) {
      errorMsg = errorMsg + ", Gender";
    }
    if (!formData.addressLine1.trim()) {
      errorMsg = errorMsg + ", Address Line 1";
    }
    if (!formData.street.trim()) {
      errorMsg = errorMsg + ", Street";
    }
    if (!formData.city.trim()) {
      errorMsg = errorMsg + ", City";
    }
    if (!formData.guardianName.trim()) {
      errorMsg = errorMsg + ", Parent Name";
    }
    if (!formData.relationship.trim()) {
      errorMsg = errorMsg + ", Relationship";
    }
    if (!formData.guardianContactNo.trim()) {
      errorMsg = errorMsg + ", Parent Contact Number";
    }

    if (errorMsg.length > 0) {
      showErrorToastMsg("Required " + errorMsg);
      return;
    }

    setSaving(true);

    try {
      const params = {
        userId: studentData.UserId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        nic: formData.nic.trim(),
        email: formData.email.trim(),
        birthday: formData.birthday,
        gender: Number(formData.gender),
        addressLine1: formData.addressLine1.trim(),
        street: formData.street.trim(),
        city: formData.city.trim(),
        guardianName: formData.guardianName.trim(),
        relationship: formData.relationship,
        postalCode: formData.postalCode,
        guardianContactNo: formData.guardianContactNo.trim(),
        isPhysical: formData.isPhysical === "1",
        institute: formData.isPhysical === "1" ? formData.institute : "",
        district: formData.district,
        batch: formData.batch,
        school: formData.school.trim(),
        indexNo: formData.indexNo.trim(),
      };

      var res;
      if (isAdminView) {
        res = await updateStudentAPI(params);
      } else {
        res = await updateMyDetailsAPI(params);
      }
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Student updated successfully");
      } else {
        showErrorToastMsg(res?.Message || "Failed to update student");
      }
    } catch (error) {
      console.error("Error updating student:", error);
      showErrorToastMsg("Failed to update student");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  if (studentData === null) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-primary-600"></div>
    );
  }
  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Personal Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                NIC <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nic"
                value={formData.nic}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Birthday <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="0">Select Gender</option>
                <option value="1">Male</option>
                <option value="2">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code
              </label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class Type <span className="text-red-500">*</span>
              </label>
              <select
                name="isPhysical"
                value={formData.isPhysical}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Class Type</option>
                <option value="1">Physical</option>
                <option value="0">Online</option>
              </select>
            </div>

            {formData.isPhysical === "1" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institute <span className="text-red-500">*</span>
                </label>
                <select
                  name="institute"
                  value={formData.institute}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Institute</option>
                  <option value="Science Center - Kegalle">Science Center - Kegalle</option>
                  <option value="Sesip - Rambukkana">Sesip - Rambukkana</option>
                  <option value="Unique - Matale">Unique - Matale</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                District <span className="text-red-500">*</span>
              </label>
              <select
                name="district"
                value={formData.district}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                // required
              >
                <option value="">Select District</option>
                <option value="Ampara">Ampara</option>
                <option value="Anuradhapura">Anuradhapura</option>
                <option value="Badulla">Badulla</option>
                <option value="Batticaloa">Batticaloa</option>
                <option value="Colombo">Colombo</option>
                <option value="Galle">Galle</option>
                <option value="Gampaha">Gampaha</option>
                <option value="Hambantota">Hambantota</option>
                <option value="Jaffna">Jaffna</option>
                <option value="Kalutara">Kalutara</option>
                <option value="Kandy">Kandy</option>
                <option value="Kegalle">Kegalle</option>
                <option value="Kilinochchi">Kilinochchi</option>
                <option value="Kurunegala">Kurunegala</option>
                <option value="Mannar">Mannar</option>
                <option value="Matale">Matale</option>
                <option value="Matara">Matara</option>
                <option value="Moneragala">Moneragala</option>
                <option value="Mullaitivu">Mullaitivu</option>
                <option value="Nuwara Eliya">Nuwara Eliya</option>
                <option value="Polonnaruwa">Polonnaruwa</option>
                <option value="Puttalam">Puttalam</option>
                <option value="Ratnapura">Ratnapura</option>
                <option value="Trincomalee">Trincomalee</option>
                <option value="Vavuniya">Vavuniya</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch <span className="text-red-500">*</span>
              </label>
              <select
                name="batch"
                value={formData.batch}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                // required
              >
                <option value="">Select Batch</option>
                <option value="Batch 2026">Batch 2026</option>
                <option value="Batch 2027">Batch 2027</option>
                <option value="Batch 2028">Batch 2028</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                School <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="school"
                value={formData.school}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                // required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Index Number
              </label>
              <input
                type="text"
                name="indexNo"
                value={formData.indexNo}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Parent Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="guardianName"
                value={formData.guardianName}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Relationship <span className="text-red-500">*</span>
              </label>
              <select
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Relationship</option>
                <option value="father">Father</option>
                <option value="mother">Mother</option>
                <option value="guardian">Guardian</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parent Contact Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="guardianContactNo"
                value={formData.guardianContactNo}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="submit"
            disabled={saving}
            className={`text-sm font-medium px-4 py-2 rounded-md transition-colors ${
              saving
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-primary-600 text-white hover:bg-primary-700"
            }`}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentDetailsManage;
